/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import {
  Combobox,
  InputBase,
  useMantineTheme,
  Text,
  rem,
  type ComboboxStore,
  Group,
} from '@mantine/core';
import { RiExpandUpDownLine } from '@remixicon/react';

import { useTeamName } from '@fai/friendli-suite/features/settings/team-settings/use-team';
import { input } from '@fai/friendli-suite/views/layout/product/basic-header.css';
import TeamAvatar from '@fai/ui/components/avatar/team';
import { TextVariants } from '@fai/ui/components/typography/variants';
import type { PropsWithChildren } from '@fai/util/types';

import DropDown from './drop-down';

function Target({ combobox, teamId }: { combobox: ComboboxStore; teamId: string }) {
  const theme = useMantineTheme();
  const { teamName } = useTeamName(teamId);

  return (
    <Combobox.Target>
      <InputBase
        classNames={{ input }}
        component="button"
        maw={rem(240)}
        pointer={true}
        px={rem(1)}
        radius={theme.pf.radius.pill}
        rightSection={<RiExpandUpDownLine color={theme.pf.color.fg.neutral.default} size={16} />}
        rightSectionPointerEvents="none"
        type="button"
        onClick={() => combobox.toggleDropdown()}
      >
        <Group gap={theme.pf.layout['3xs']} wrap="nowrap">
          <TeamAvatar size={28} />
          <Text
            c={theme.pf.color.fg.neutral.strong}
            style={{
              whiteSpace: 'nowrap',
            }}
            truncate="end"
            variant={TextVariants.TitleXs}
          >
            {teamName}
          </Text>
        </Group>
      </InputBase>
    </Combobox.Target>
  );
}

// TODO: fix team's icon
export default function TeamSelect({
  children,
  combobox,
  handleOptionSubmit,
}: PropsWithChildren<{
  combobox: ComboboxStore;
  handleOptionSubmit: (newValue: string) => void;
}>) {
  const theme = useMantineTheme();

  return (
    <Combobox
      offset={{ mainAxis: 4 }}
      position="bottom-start"
      resetSelectionOnOptionHover={true}
      shadow={theme.pf.shadow['shadow-md']}
      store={combobox}
      withinPortal={false}
      onOptionSubmit={handleOptionSubmit}
    >
      {children}
    </Combobox>
  );
}

TeamSelect.displayName = 'TeamSelect';

TeamSelect.Target = Target;
TeamSelect.DropDown = DropDown;
